import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Users,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  DollarSign,
  ShoppingCart,
  BarChart3,
  Filter,
  Download,
  MoreVertical,
  Search,
  Bell,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Edit,
  Trash2,
  ChevronRight,
  Grid,
  List,
  Menu,
  X,
  Home,
  Settings,
  UserPlus,
  PlusCircle,
  FileText
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/ui/Card';
import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import DataTable from '../../../components/ui/DataTable';
import StatCard from '../../../components/dashboard/StatCard';
import Chart from '../../../components/dashboard/Chart';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('week');
  const [viewMode, setViewMode] = useState('grid');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Mock data
  const stats = {
    totalProducts: { value: 2456, change: 12.5, trend: 'up' },
    totalUsers: { value: 12345, change: 8.2, trend: 'up' },
    totalRevenue: { value: 892450, change: 25.3, trend: 'up' },
    pendingApprovals: { value: 124, change: -3, trend: 'down' },
    conversionRate: { value: 3.2, change: 0.8, trend: 'up' },
    avgOrderValue: { value: 245, change: 15.2, trend: 'up' }
  };

  const recentActivities = [
    { id: 1, user: 'John Smith', action: 'New product added', type: 'product', time: '2 min ago', status: 'success' },
    { id: 2, user: 'Sarah Johnson', action: 'Account verified', type: 'user', time: '15 min ago', status: 'success' },
    { id: 3, user: 'Mike Wilson', action: 'High-value order placed', type: 'order', time: '30 min ago', status: 'info' },
    { id: 4, user: 'Emma Davis', action: 'Product approval pending', type: 'product', time: '1 hour ago', status: 'warning' },
    { id: 5, user: 'Robert Brown', action: 'Report submitted', type: 'report', time: '2 hours ago', status: 'info' }
  ];

  const topProducts = [
    { id: 1, name: 'Premium Coffee Beans', sales: 2450, revenue: 112540, growth: 25 },
    { id: 2, name: 'Organic Green Tea', sales: 1890, revenue: 85050, growth: 18 },
    { id: 3, name: 'Spices Collection', sales: 1560, revenue: 140400, growth: 32 },
    { id: 4, name: 'Rice 50kg Bag', sales: 1340, revenue: 160800, growth: 12 },
    { id: 5, name: 'Olive Oil Extra Virgin', sales: 980, revenue: 73710, growth: 8 }
  ];

  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Revenue ($)',
        data: [12500, 18900, 15200, 21000, 24500, 18900, 23000],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Orders',
        data: [120, 189, 152, 210, 245, 189, 230],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts.value.toLocaleString(),
      change: stats.totalProducts.change,
      trend: stats.totalProducts.trend,
      icon: Package,
      color: 'blue',
      link: '/admin/products'
    },
    {
      title: 'Total Users',
      value: stats.totalUsers.value.toLocaleString(),
      change: stats.totalUsers.change,
      trend: stats.totalUsers.trend,
      icon: Users,
      color: 'green',
      link: '/admin/users'
    },
    {
      title: 'Total Revenue',
      value: `$${stats.totalRevenue.value.toLocaleString()}`,
      change: stats.totalRevenue.change,
      trend: stats.totalRevenue.trend,
      icon: DollarSign,
      color: 'purple',
      link: '/admin/reports'
    },
    {
      title: 'Pending Approvals',
      value: stats.pendingApprovals.value.toLocaleString(),
      change: stats.pendingApprovals.change,
      trend: stats.pendingApprovals.trend,
      icon: AlertCircle,
      color: 'orange',
      link: '/admin/moderation'
    }
  ];

  const quickActions = [
    { title: 'Add Product', icon: PlusCircle, link: '/admin/products/add', color: 'blue' },
    { title: 'Add User', icon: UserPlus, link: '/admin/users/add', color: 'green' },
    { title: 'Send Message', icon: MessageSquare, link: '/admin/chats/compose', color: 'purple' },
    { title: 'Generate Report', icon: FileText, link: '/admin/reports', color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      {/* <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
              <p className="text-xs text-gray-500">Welcome back</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Search className="h-5 w-5 text-gray-600" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 relative">
              <Bell className="h-5 w-5 text-gray-600" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
            </button>
          </div>
        </div>

        {isSearchOpen && (
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search..."
                className="pl-10 w-full"
                autoFocus
              />
            </div>
          </div>
        )}
      </div> */}

      {/* Mobile Sidebar */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsSidebarOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Menu</h2>
              <button 
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <Link to="/admin/dashboard" className="flex items-center p-3 rounded-lg bg-blue-50 text-blue-600">
                <Home className="h-5 w-5 mr-3" />
                Dashboard
              </Link>
              <Link to="/admin/products" className="flex items-center p-3 rounded-lg hover:bg-gray-100">
                <Package className="h-5 w-5 mr-3" />
                Products
              </Link>
              <Link to="/admin/users" className="flex items-center p-3 rounded-lg hover:bg-gray-100">
                <Users className="h-5 w-5 mr-3" />
                Users
              </Link>
              <Link to="/admin/settings" className="flex items-center p-3 rounded-lg hover:bg-gray-100">
                <Settings className="h-5 w-5 mr-3" />
                Settings
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 lg:p-6">
        {/* Desktop Header */}
        <div className="hidden lg:block mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
              <p className="text-gray-600">Welcome back, {user?.name || 'Admin'}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search dashboard..."
                  className="pl-10 w-64"
                />
              </div>
              <Button variant="outline" size="small">
                <Calendar className="h-4 w-4 mr-2" />
                {timeRange.charAt(0).toUpperCase() + timeRange.slice(1)}
              </Button>
              <Button variant="primary" size="small">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link key={index} to={stat.link}>
                <Card className="p-4 hover:shadow-lg transition-all duration-300 cursor-pointer h-full">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
                      <div className="flex items-center">
                        {stat.trend === 'up' ? (
                          <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-xs sm:text-sm font-medium ${
                          stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {stat.change > 0 ? '+' : ''}{stat.change}% from last week
                        </span>
                      </div>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-lg ${
                      stat.color === 'blue' ? 'bg-blue-50' :
                      stat.color === 'green' ? 'bg-green-50' :
                      stat.color === 'purple' ? 'bg-purple-50' : 'bg-orange-50'
                    }`}>
                      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${
                        stat.color === 'blue' ? 'text-blue-600' :
                        stat.color === 'green' ? 'text-green-600' :
                        stat.color === 'purple' ? 'text-purple-600' : 'text-orange-600'
                      }`} />
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Charts and Quick Stats */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {/* Revenue Chart */}
          <div className="lg:col-span-2">
            <Card className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Revenue Analytics</h3>
                  <p className="text-xs sm:text-sm text-gray-500">Weekly performance overview</p>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 mt-2 sm:mt-0">
                  {['week', 'month', 'quarter'].map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        timeRange === range
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {range.charAt(0).toUpperCase() + range.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-48 sm:h-64 md:h-72 lg:h-64 xl:h-72">
                <Chart data={chartData} type="line" height="100%" />
              </div>
            </Card>
          </div>

          {/* Quick Stats */}
          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Quick Stats</h3>
            <div className="space-y-3 sm:space-y-4">
              {[
                { label: 'Orders Today', value: '245', icon: ShoppingCart, color: 'green', trend: 'up' },
                { label: 'New Users', value: '48', icon: Users, color: 'blue', trend: 'up' },
                { label: 'Avg. Order Value', value: `$${stats.avgOrderValue.value}`, icon: DollarSign, color: 'purple', trend: 'up' },
                { label: 'Conversion Rate', value: `${stats.conversionRate.value}%`, icon: BarChart3, color: 'orange', trend: 'up' }
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg mr-3 ${
                        stat.color === 'green' ? 'bg-green-50' :
                        stat.color === 'blue' ? 'bg-blue-50' :
                        stat.color === 'purple' ? 'bg-purple-50' : 'bg-orange-50'
                      }`}>
                        <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${
                          stat.color === 'green' ? 'text-green-600' :
                          stat.color === 'blue' ? 'text-blue-600' :
                          stat.color === 'purple' ? 'text-purple-600' : 'text-orange-600'
                        }`} />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.label}</p>
                        <p className="text-lg sm:text-xl font-bold text-gray-900">{stat.value}</p>
                      </div>
                    </div>
                    {stat.trend === 'up' ? (
                      <ArrowUpRight className={`h-4 w-4 sm:h-5 sm:w-5 ${
                        stat.color === 'green' ? 'text-green-500' :
                        stat.color === 'blue' ? 'text-blue-500' :
                        stat.color === 'purple' ? 'text-purple-500' : 'text-orange-500'
                      }`} />
                    ) : (
                      <ArrowDownRight className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Recent Activity and Top Products */}
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
          {/* Recent Activity */}
          <Card className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activity</h3>
                <p className="text-xs sm:text-sm text-gray-500">Latest platform activities</p>
              </div>
              <Link to="/admin/activity" className="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-500 flex items-center">
                View all <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
              </Link>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center mr-2 sm:mr-3 ${
                      activity.status === 'success' ? 'bg-green-50' :
                      activity.status === 'warning' ? 'bg-yellow-50' :
                      'bg-blue-50'
                    }`}>
                      {activity.type === 'product' ? (
                        <Package className={`h-4 w-4 sm:h-5 sm:w-5 ${
                          activity.status === 'success' ? 'text-green-600' :
                          activity.status === 'warning' ? 'text-yellow-600' :
                          'text-blue-600'
                        }`} />
                      ) : activity.type === 'user' ? (
                        <Users className={`h-4 w-4 sm:h-5 sm:w-5 ${
                          activity.status === 'success' ? 'text-green-600' :
                          activity.status === 'warning' ? 'text-yellow-600' :
                          'text-blue-600'
                        }`} />
                      ) : (
                        <DollarSign className={`h-4 w-4 sm:h-5 sm:w-5 ${
                          activity.status === 'success' ? 'text-green-600' :
                          activity.status === 'warning' ? 'text-yellow-600' :
                          'text-blue-600'
                        }`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm sm:text-base font-medium text-gray-900 truncate">{activity.user}</div>
                      <div className="text-xs sm:text-sm text-gray-500 truncate">{activity.action}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap ml-2">{activity.time}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Products */}
          <Card className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Products</h3>
                <p className="text-xs sm:text-sm text-gray-500">Best performing products</p>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 sm:p-2 rounded-lg ${
                    viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Grid className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 sm:p-2 rounded-lg ${
                    viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <List className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
              </div>
            </div>
            
            {viewMode === 'list' ? (
              <div className="space-y-3">
                {topProducts.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center min-w-0">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                        <Package className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm sm:text-base font-medium text-gray-900 truncate">{product.name}</div>
                        <div className="text-xs text-gray-500">{product.sales.toLocaleString()} sales</div>
                      </div>
                    </div>
                    <div className="text-right ml-2">
                      <div className="text-sm sm:text-base font-semibold text-green-600">${(product.revenue / 1000).toFixed(0)}k</div>
                      <div className={`text-xs ${product.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {product.growth > 0 ? '+' : ''}{product.growth}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {topProducts.map((product) => (
                  <div key={product.id} className="bg-gradient-to-br from-gray-50 to-white p-3 sm:p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-2 sm:mb-3">
                      <div className="h-6 w-6 sm:h-8 sm:w-8 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                        <Package className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                      </div>
                      <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{product.name}</div>
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Revenue</span>
                        <span className="text-xs sm:text-sm font-semibold text-green-600">${(product.revenue / 1000).toFixed(0)}k</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Growth</span>
                        <span className={`text-xs font-medium ${product.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {product.growth > 0 ? '+' : ''}{product.growth}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Link key={index} to={action.link} className="group">
                  <div className={`p-3 sm:p-4 rounded-lg text-center hover:shadow-md transition-all duration-300 ${
                    action.color === 'blue' ? 'bg-gradient-to-br from-blue-50 to-blue-100' :
                    action.color === 'green' ? 'bg-gradient-to-br from-green-50 to-green-100' :
                    action.color === 'purple' ? 'bg-gradient-to-br from-purple-50 to-purple-100' :
                    'bg-gradient-to-br from-orange-50 to-orange-100'
                  }`}>
                    <div className="bg-white p-2 sm:p-3 rounded-lg inline-flex mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300">
                      <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${
                        action.color === 'blue' ? 'text-blue-600' :
                        action.color === 'green' ? 'text-green-600' :
                        action.color === 'purple' ? 'text-purple-600' : 'text-orange-600'
                      }`} />
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-gray-900">{action.title}</div>
                    <div className="text-xs text-gray-500 mt-1 hidden sm:block">
                      {action.title.includes('Product') ? 'Create new product' :
                       action.title.includes('User') ? 'Create new user' :
                       action.title.includes('Message') ? 'Compose message' : 'View analytics'}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;